/**
 * Sticky page header for the Accounting Recurring transactions page.
 *
 * Rendered via the AppLayout pageHeader slot (lives OUTSIDE the main
 * scroll area, so it is always pinned at the top of the content card
 * with no position: sticky needed).
 *
 * Contains, left to right:
 *   - Breadcrumb (Accounting / Recurring transactions)
 *   - Page title
 *   - Right-side actions: Reminder List dropdown + "+ New" primary button
 *
 * No utility bar (All Lists / Feedback / open-in-new / fullscreen).
 * Those were added by mistake; this file matches Claire's approved
 * PR 10 preview exactly.
 *
 * The Reminder List dropdown shares state with the page body through
 * the URL ?mode= search param. The "+ New" button sets ?action=new,
 * which the page body watches to open the Recurring Builder drawer.
 */

import type { CSSProperties } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, Plus } from "lucide-react";
import type { RecurringMode } from "../recurringTypes";

/**
 * View modes exposed in the Reminder List dropdown. Values map 1:1
 * to RecurringMode but kept as a URL-safe string type so the header
 * and the page body can share state through useSearchParams.
 */
export type RecurringViewMode = "reminder" | "scheduled" | "unscheduled";

const VIEW_MODE_OPTIONS: Array<{ value: RecurringViewMode; label: string }> = [
  { value: "reminder", label: "Reminder List" },
  { value: "scheduled", label: "Scheduled" },
  { value: "unscheduled", label: "Unscheduled" },
];

/**
 * Convert the URL view mode into the RecurringMode value used by
 * the filter. Currently 1:1; kept as a helper so the mapping stays
 * in one place if either side's naming changes later.
 */
export function urlModeToFilterMode(view: RecurringViewMode): RecurringMode {
  return view;
}

export function RecurringHeader() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentView =
    (searchParams.get("mode") as RecurringViewMode | null) ?? "reminder";

  const handleModeChange = (next: RecurringViewMode) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("mode", next);
    setSearchParams(nextParams, { replace: true });
  };

  const handleNewClick = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("action", "new");
    setSearchParams(nextParams);
  };

  return (
    <div style={headerStyle}>
      <div style={breadcrumbStyle}>
        Accounting
        <span style={breadcrumbSepStyle}>/</span>
        <span style={breadcrumbCurrentStyle}>Recurring transactions</span>
      </div>
      <div style={rowStyle}>
        <h2 style={titleStyle}>Recurring transactions</h2>
        <div style={actionsStyle}>
          <div style={selectWrapStyle}>
            <select
              value={currentView}
              onChange={(e) =>
                handleModeChange(e.target.value as RecurringViewMode)
              }
              style={selectStyle}
              aria-label="View mode"
            >
              {VIEW_MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} style={selectCaretStyle} />
          </div>
          <button
            type="button"
            onClick={handleNewClick}
            style={primaryBtnStyle}
          >
            <Plus size={13} strokeWidth={2.4} />
            New
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Styles ----------

const headerStyle: CSSProperties = {
  padding: "20px 28px 16px",
  background: "#ffffff",
  borderBottom: "1px solid #E7EAF0",
};

const breadcrumbStyle: CSSProperties = {
  fontSize: 12,
  color: "#3A4A52",
  marginBottom: 4,
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
};

const breadcrumbSepStyle: CSSProperties = {
  color: "#6B7684",
  margin: "0 5px",
};

const breadcrumbCurrentStyle: CSSProperties = {
  color: "#0E1A1A",
  fontWeight: 600,
};

const rowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
};

const titleStyle: CSSProperties = {
  fontSize: 22,
  fontWeight: 800,
  margin: 0,
  letterSpacing: "-0.015em",
  color: "#0E1A1A",
};

const actionsStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
};

const selectWrapStyle: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
};

const selectStyle: CSSProperties = {
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  background: "#ffffff",
  border: "1px solid #D8DEE6",
  borderRadius: 8,
  padding: "8px 32px 8px 14px",
  fontSize: 13,
  fontWeight: 600,
  color: "#0E1A1A",
  cursor: "pointer",
  minWidth: 160,
  fontFamily: "inherit",
  outline: "none",
};

const selectCaretStyle: CSSProperties = {
  position: "absolute",
  right: 10,
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  color: "#3A4A52",
};

const primaryBtnStyle: CSSProperties = {
  background: "#0E1A1A",
  color: "#ffffff",
  border: "none",
  borderRadius: 8,
  padding: "9px 16px",
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
};

export default RecurringHeader;